import { useState } from 'react';
import {
  Maximize2Icon,
  MessageSquareIcon,
  MessageSquareTextIcon,
  Minimize2Icon,
  PackageIcon,
  XIcon,
} from 'lucide-react';
import {
  Avatar,
  EmptyState,
  Pill,
  RichText,
  RichTextEditor,
  type MentionCandidate,
  type RichTextDoc,
} from '../../index.js';

/** One message on the thread, as the workspace endpoint returns it. */
export interface ThreadMessage {
  readonly id: string;
  readonly body: string;
  readonly content: RichTextDoc;
  readonly authorName: string;
  readonly authorKind: 'CLIENT' | 'BCE';
  readonly mentions: readonly { readonly membershipId: string; readonly name: string }[];
  readonly createdAt: string;
  readonly mine: boolean;
  readonly kind: 'NOTE' | 'SHIPMENT_REPORT';
  readonly parentId: string | null;
}

export interface MessageThreadProps {
  readonly messages: readonly ThreadMessage[];
  readonly mentionCandidates: readonly MentionCandidate[];
  /** Left out for someone who may only read: no message box and no reply box. */
  readonly onPost?: (message: { readonly content: RichTextDoc; readonly parentId?: string }) => void;
  readonly isPosting: boolean;
  /** What the main box invites, e.g. "Message BCE and your team". */
  readonly placeholder: string;
  /** Text the main box starts from, e.g. a mention of the person a "Message" button was pressed on. */
  readonly draft?: { readonly key: string; readonly doc: RichTextDoc } | undefined;
  /** What an empty thread says; the default speaks to the client. */
  readonly empty?: { readonly title: string; readonly message: string } | undefined;
}

const DEFAULT_EMPTY = {
  title: 'No messages yet',
  message: 'Ask BCE a question, or leave a note for a colleague. Everything about this event stays in one place.',
} as const;

/** Messages from the same person this close together share one header, as in Slack. */
const GROUP_WINDOW_MS = 5 * 60 * 1000;

function dayKey(iso: string): string {
  return new Date(iso).toDateString();
}

function dayLabel(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' }),
  });
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function ago(iso: string): string {
  const minutes = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** An icon button on the actions bar, with the label as a tooltip above it. */
function ActionButton({
  label,
  onClick,
  children,
}: {
  readonly label: string;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <span className="group/action relative">
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        className="grid size-7 place-items-center rounded-portal-button text-portal-muted hover:bg-portal-sky hover:text-portal-ink focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
      >
        {children}
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute -top-9 right-0 rounded-portal-button bg-portal-ink px-2 py-1 text-portal-micro font-bold whitespace-nowrap text-white opacity-0 shadow-portal-card transition-opacity group-hover/action:opacity-100 group-focus-within/action:opacity-100"
      >
        {label}
      </span>
    </span>
  );
}

/** One message row: avatar and header on the first of a run, body on every one. */
function MessageRow({
  message,
  continued,
  replies,
  onOpenThread,
  inThread = false,
}: {
  readonly message: ThreadMessage;
  /** True when the previous row is the same person moments earlier: no avatar, no header. */
  readonly continued: boolean;
  readonly replies?: readonly ThreadMessage[];
  readonly onOpenThread?: (id: string) => void;
  readonly inThread?: boolean;
}): React.ReactElement {
  const report = message.kind === 'SHIPMENT_REPORT';
  const replyCount = replies?.length ?? 0;
  const lastReply = replies?.[replies.length - 1];

  return (
    <div
      data-guide={inThread ? undefined : 'messages-message'}
      className={[
        'group relative flex gap-3 px-3',
        continued ? 'py-0.5' : 'pt-3 pb-0.5',
        'hover:bg-portal-sky/40 focus-within:bg-portal-sky/40',
      ].join(' ')}
    >
      <div className="w-9 shrink-0">
        {continued ? null : (
          <Avatar name={message.authorName} size={36} />
        )}
      </div>

      <div className="min-w-0 flex-1">
        {continued ? null : (
          <div className="flex flex-wrap items-baseline gap-x-2">
            <b className="text-portal-small">{message.authorName}</b>
            {message.authorKind === 'BCE' ? <Pill tone="brand">BCE</Pill> : null}
            {report ? (
              <span className="inline-flex items-center gap-1 text-portal-micro font-bold text-portal-muted">
                <PackageIcon aria-hidden className="size-3.5" />
                Shipment problem
              </span>
            ) : null}
            <span className="text-portal-micro text-portal-muted">{clock(message.createdAt)}</span>
          </div>
        )}
        <RichText doc={message.content} className="text-portal-small leading-[1.55] break-words" />

        {!inThread && onOpenThread && replyCount > 0 && lastReply ? (
          <button
            type="button"
            onClick={() => onOpenThread(message.id)}
            className="mt-1.5 flex items-center gap-2 text-portal-micro"
          >
            <span className="flex -space-x-1.5">
              {[...new Set(replies?.map((reply) => reply.authorName))].slice(0, 3).map((name) => (
                <span key={name} className="rounded-full ring-2 ring-white">
                  <Avatar name={name} size={18} />
                </span>
              ))}
            </span>
            <span className="font-bold text-brand-link underline-offset-2 hover:underline">
              {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
            </span>
            <span className="text-portal-muted">Last reply {ago(lastReply.createdAt)}</span>
          </button>
        ) : null}
      </div>

      {!inThread && onOpenThread ? (
        <div
          role="toolbar"
          aria-label="Message actions"
          className="absolute -top-3 right-3 flex items-center gap-0.5 rounded-portal-card border border-portal-line bg-white p-0.5 opacity-0 shadow-portal-card transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
        >
          <ActionButton label="Reply in thread" onClick={() => onOpenThread(message.id)}>
            <MessageSquareTextIcon aria-hidden className="size-4" />
          </ActionButton>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The event's conversation, laid out the way people already know from Slack:
 * a flat list under day dividers, one header per run of messages from the
 * same person, and replies kept in a thread panel beside the conversation
 * rather than nested in it. Light, on the portal's own surfaces.
 *
 * @param props - The messages, who can be tagged, and the post action.
 * @returns The conversation.
 */
export function MessageThread({
  messages,
  mentionCandidates,
  onPost,
  isPosting,
  placeholder,
  draft,
  empty = DEFAULT_EMPTY,
}: MessageThreadProps): React.ReactElement {
  const [openThread, setOpenThread] = useState<string | null>(null);
  // Expanded, the thread takes the whole width and the conversation steps
  // aside — for a long thread, or one being read closely.
  const [expanded, setExpanded] = useState(false);

  const topLevel = messages.filter((message) => message.parentId === null);
  const repliesOf = (id: string): readonly ThreadMessage[] =>
    messages.filter((message) => message.parentId === id);
  const parent = openThread ? (messages.find((message) => message.id === openThread) ?? null) : null;

  // Rows with their day dividers and "continued" flags worked out once.
  const rows: ({ kind: 'day'; label: string; key: string } | { kind: 'message'; message: ThreadMessage; continued: boolean })[] = [];
  let previous: ThreadMessage | null = null;
  for (const message of topLevel) {
    if (previous === null || dayKey(previous.createdAt) !== dayKey(message.createdAt)) {
      rows.push({ kind: 'day', label: dayLabel(message.createdAt), key: dayKey(message.createdAt) });
      previous = null;
    }
    const continued =
      previous !== null &&
      previous.authorName === message.authorName &&
      previous.kind === 'NOTE' &&
      message.kind === 'NOTE' &&
      new Date(message.createdAt).getTime() - new Date(previous.createdAt).getTime() < GROUP_WINDOW_MS;
    rows.push({ kind: 'message', message, continued });
    previous = message;
  }

  return (
    <div
      className={[
        'grid gap-0 border-t border-portal-line',
        parent && !expanded ? 'lg:grid-cols-[minmax(0,1fr)_400px]' : '',
      ].join(' ')}
    >
      <div
        className={[
          'flex min-h-[460px] flex-col px-2 pt-2 pb-3',
          parent && expanded ? 'hidden' : '',
        ].join(' ')}
      >
        <div data-guide="messages-list" className="flex-1 space-y-0.5 pb-3">
          {rows.length === 0 ? (
            <EmptyState
              icon={<MessageSquareIcon aria-hidden />}
              title={empty.title}
              message={empty.message}
            />
          ) : null}
          {rows.map((row) =>
            row.kind === 'day' ? (
              <div key={row.key} className="relative my-3 flex items-center">
                <span className="h-px flex-1 bg-portal-line" />
                <span className="rounded-portal-pill border border-portal-line bg-white px-3 py-0.5 text-portal-micro font-bold text-portal-ink">
                  {row.label}
                </span>
                <span className="h-px flex-1 bg-portal-line" />
              </div>
            ) : (
              <MessageRow
                key={row.message.id}
                message={row.message}
                continued={row.continued}
                replies={repliesOf(row.message.id)}
                onOpenThread={setOpenThread}
              />
            ),
          )}
        </div>

        {onPost ? (
          <div data-guide="messages-composer" className="px-2">
            <RichTextEditor
              ariaLabel="Write a message"
              placeholder={placeholder}
              mentionCandidates={mentionCandidates}
              onSubmit={(content) => onPost({ content })}
              disabled={isPosting}
              draft={draft}
            />
          </div>
        ) : null}
      </div>

      {parent ? (
        <aside
          aria-label="Thread"
          className={[
            'flex min-h-[460px] flex-col bg-white',
            expanded ? '' : 'border-t border-portal-line lg:border-t-0 lg:border-l',
          ].join(' ')}
        >
          <div className="flex items-center justify-between border-b border-portal-line px-4 py-2.5">
            <h3 className="text-portal-small font-bold">Thread</h3>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setExpanded((current) => !current)}
                aria-label={expanded ? 'Shrink thread' : 'Expand thread'}
                aria-pressed={expanded}
                className="rounded-portal-button p-1 text-portal-muted hover:bg-portal-sky hover:text-portal-ink"
              >
                {expanded ? (
                  <Minimize2Icon aria-hidden className="size-4" />
                ) : (
                  <Maximize2Icon aria-hidden className="size-4" />
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setOpenThread(null);
                  setExpanded(false);
                }}
                aria-label="Close thread"
                className="rounded-portal-button p-1 text-portal-muted hover:bg-portal-sky hover:text-portal-ink"
              >
                <XIcon aria-hidden className="size-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 space-y-0.5 py-2">
            <MessageRow message={parent} continued={false} inThread />
            {repliesOf(parent.id).length > 0 ? (
              <div className="flex items-center gap-3 px-3 pt-2 text-portal-micro text-portal-muted">
                {repliesOf(parent.id).length}{' '}
                {repliesOf(parent.id).length === 1 ? 'reply' : 'replies'}
                <span className="h-px flex-1 bg-portal-line" />
              </div>
            ) : null}
            {repliesOf(parent.id).map((reply, index, all) => {
              const before = all[index - 1];
              const continued =
                before !== undefined &&
                before.authorName === reply.authorName &&
                new Date(reply.createdAt).getTime() - new Date(before.createdAt).getTime() < GROUP_WINDOW_MS;
              return <MessageRow key={reply.id} message={reply} continued={continued} inThread />;
            })}
          </div>
          {onPost ? (
            <div className="border-t border-portal-line px-4 pt-3 pb-3">
              <RichTextEditor
                ariaLabel="Write a reply"
                placeholder="Reply…"
                mentionCandidates={mentionCandidates}
                onSubmit={(content) => onPost({ content, parentId: parent.id })}
                disabled={isPosting}
                submitLabel="Reply"
              />
            </div>
          ) : null}
        </aside>
      ) : null}
    </div>
  );
}
